import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faEye,
  faArrowUpFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import axios from "axios";
import { Tooltip } from "@mui/material";
import { Toaster, toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { addEventRegistration } from "actions/action";
import { updateEventRegistration } from "actions/action";
import SubmittingModal from "components/Modal";
import ImageCarouselInfo from "components/ImageCarousalInfo";

export default function StudentEventRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [fileInputs, setFileInputs] = useState({});

  const validateFile = (file) => {
    const isImage = file.type.startsWith('image/');
    const isPPT = file.type === 'application/vnd.ms-powerpoint' ||
      file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

    if (isImage) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`Image ${file.name} exceeds 2MB limit.`);
        return false;
      }
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 1920 || img.height > 1080) {
            toast.error(`Image ${file.name} exceeds 1920x1080 resolution.`);
            resolve(false);
          } else {
            resolve(true);
          }
        };
        img.onerror = () => resolve(true); // Ignore error and allow
        img.src = URL.createObjectURL(file);
      });
    }

    if (isPPT) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`PPT ${file.name} exceeds 10MB limit.`);
        return false;
      }
    }

    return true;
  };

  const handleInputChange = async (e, name) => {
    if (e.target.type === "file") {
      const files = Array.from(e.target.files);
      const validFiles = [];

      for (const file of files) {
        const isValid = await validateFile(file);
        if (isValid) validFiles.push(file);
      }

      if (validFiles.length > 0) {
        if (name === "image_upload") {
          setFormData({ ...formData, [name]: validFiles }); // Store FileList or array
          setFileInputs({ ...fileInputs, [name]: validFiles });
        } else {
          // For single file dynamic inputs
          setFormData({ ...formData, [name]: validFiles[0] });
          setFileInputs({ ...fileInputs, [name]: validFiles[0] });
        }
      } else {
        e.target.value = null;
      }
    } else {
      setFormData({ ...formData, [name]: e.target.value });
    }
  };
  const [eventData, setEventData] = useState({
    banner: null,
    created_by: "",
    description: "",
    end_date: "",
    event_id: "",
    event_name: "",
    form_inputs: [],
    start_date: "",
  });
  const { type, id: eventId, formValues, event } = location.state;

  let data;

  useEffect(() => {
    if (eventId) {
      getEventDataById(eventId);
      if (type === "edit") {
        if (formValues.data?.uploads) {
          const { uploads, ...rest } = formValues.data;
          setFormData({ ...rest });
          //console.log(rest);
        } else {
          setFormData({ ...formValues.data });
        }
      }
    }
  }, [eventId]);

  async function getEventDataById(id) {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://api.nvcti.in/events/getEventById/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      data = response.data.data;
      const parsedInputs = JSON.parse(data.form_inputs) || [];
      setEventData({ ...data, form_inputs: parsedInputs });
    } catch (error) {
      console.error("Error fetching event data:", error);

      toast.error(error.response?.data?.message || "Something went wrong");
    }
  }



  const removeFile = (fieldName) => {
    setFileInputs((prev) => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

  const viewFile = (fieldName) => {
    const file = fileInputs[fieldName];
    if (file) {
      if (fieldName === "image_upload" && file.length > 0) {
        // Handle multiple files for images
        const fileURL = URL.createObjectURL(file[0]);
        window.open(fileURL, "_blank");
      } else if (file instanceof File) {
        // Handle single file
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      }
    }
  };

  async function onSubmitData(e) {
    e.preventDefault();

    if (!eventData) {
      toast.error("Event data not loaded yet.");
      return;
    }
    setLoading(true);
    const payload = new FormData();

    eventData.form_inputs.forEach((input) => {
      const key = input.fieldName.trim().replace(/\s+/g, "_").toLowerCase();
      const value = formData[key];
      if (input.fieldType !== "file") payload.append(key, value || "");
    });

    for (const [key, value] of Object.entries(fileInputs)) {
      if (key === "image_upload" && value && value.length > 0) {
        // Handle multiple image files
        for (let i = 0; i < value.length; i++) {
          payload.append("file_custom_names", `${key}_${i}`);
          payload.append("files", value[i]);
        }
      } else if (value instanceof File || value instanceof Blob) {
        // Handle single files
        payload.append("file_custom_names", key);
        payload.append("files", value);
      }
    }

    // Append additional metadata directly to payload
    const user = localStorage.getItem("user_id");
    payload.append("form_data", JSON.stringify(formData));
    payload.append(
      "event_registration_id",
      type === "edit" ? formValues.id : 0
    );
    payload.append("user_id", Number(user));
    payload.append("event_id", eventData.event_id || 0);
    payload.append("status", "not_shortlisted");
    //console.log("kv",JSON.stringify(formData));

    try {
      let response;
      if (type === "create") {
        response = await addEventRegistration(payload);
      } else {
        response = await updateEventRegistration(payload, formValues.id);
      }
      //console.log(response);
      if (response.status === 200 || response.status === 201) {
        toast.success(
          type === "edit"
            ? "Event Registration Updated Successfully!"
            : "Event Registered Successfully!"
        );
        navigate(-1);
      } else {
        const errorMessage =
          response?.err?.response?.data?.message || "Something went wrong!";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Network Error:", error);
      toast.error("Network error. Please try again later.");
    }
    setLoading(false);
  }

  return (
    <>
      <Toaster position="bottom-left" reverseOrder={false} />
      <SubmittingModal loading={loading} />
      <div className="flex justify-center items-center">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 mt-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
          <div className="rounded-t bg-white mb-0 px-6 py-6">
            <div className="text-center flex justify-between">
              <h6 className="text-blueGray-700 text-xl font-semibold">
                {/* {type === "create" ? "Create" : "Update"} */}
                Event Name - {eventData?.event_name || event?.name || "Loading..."}
              </h6>
              <Tooltip title="View Applied Registrations" arrow>
                <button
                  className="bg-lightBlue-500 mb-0 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/student/viewEventRegistration`, {
                      state: { eventId },
                    });
                  }}
                >
                  View Registrations
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
            <form onSubmit={onSubmitData}>
              <h6 className="text-blueGray-700 text-xl mt-3 mb-6 font-semibold ">
                {" "}
                Description
              </h6>
              <div className="flex flex-wrap">
                <div className="flex justify-center w-full mb-3">
                  <ImageCarouselInfo images={event.event_images} />
                </div>
                <div className="w-full lg:w-6/12 ">
                  <div className="relative w-full mb-3">
                    <span className="font-bold text-white w-40 capitalize">
                      Name :
                    </span>
                    <span className="text-white capitalize">
                      {" "}
                      {event.name}{" "}
                    </span>
                  </div>
                </div>
                <div className="w-full lg:w-6/12 ">
                  <div className="relative w-full mb-3">
                    <span className="font-bold text-white w-40 capitalize">
                      Start Date :
                    </span>
                    <span className="text-white"> {event.startDate} </span>
                  </div>
                </div>
                <div className="w-full lg:w-6/12 ">
                  <div className="relative w-full mb-3">
                    <span className="font-bold text-white w-40 capitalize">
                      End Date :
                    </span>
                    <span className="text-white"> {event.endDate} </span>
                  </div>
                </div>

                <div className="w-full lg:w-12/12">
                  <div className="relative w-full mb-3">
                    <span className="font-bold text-white w-40 capitalize">
                      Description :
                    </span>
                    <span className="text-white"> {event.description} </span>
                  </div>
                </div>
              </div>
              <hr className="mt-3" />
              <h6 className="text-blueGray-700 text-xl mt-3 mb-6 font-semibold">
                {eventData?.event_name || "Loading..."} Registration
              </h6>
              <div className="flex flex-wrap">
                {eventData.form_inputs.map((input, index) => {
                  const name = input.fieldName
                    .trim()
                    .replace(/\s+/g, "_")
                    .toLowerCase();

                  const isFile = input.fieldType === "file";

                  return (
                    <div className="w-full lg:w-6/12 px-4" key={index}>
                      <div className="relative w-full mb-3">
                        <label className="block text-white text-xs font-bold mb-2">
                          {input.fieldName}
                        </label>

                        {input.fieldType === "textarea" ? (
                          <textarea
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                            placeholder={input.fieldName}
                            name={name}
                            value={formData[name]}
                            onChange={(e) => handleInputChange(e, name)}
                          />
                        ) : input.fieldType === "select" ? (
                          <select
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                            name={name}
                            value={formData[name]}
                            onChange={(e) => handleInputChange(e, name)}
                          >
                            <option value="">Select {input.fieldName}</option>
                            {(input.options || []).map((opt, i) => (
                              <option key={i} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : isFile ? (
                          <>
                            <input
                              id={`file-input-${index}`}
                              type="file"
                              className="hidden"
                              name={name}
                              onChange={(e) => handleInputChange(e, name)}
                            />
                            <div className="flex flex-wrap items-center gap-4 mt-2">
                              <label
                                htmlFor={`file-input-${index}`}
                                className="mr-4"
                              >
                                <span className="cursor-pointer bg-lightBlue-500 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md">
                                  <FontAwesomeIcon
                                    icon={faArrowUpFromBracket}
                                  />{" "}
                                  Upload File
                                </span>
                              </label>

                              {fileInputs[name] && (
                                <div className="flex items-center">
                                  <Tooltip title="View File" arrow>
                                    <motion.button
                                      whileHover={{
                                        scale: 1.2,
                                        color: "#FACC15",
                                      }}
                                      whileTap={{ scale: 0.9 }}
                                      type="button"
                                      onClick={() => viewFile(name)}
                                      className="text-blue-500 hover:text-yellow-500 focus:outline-none mr-4"
                                    >
                                      <FontAwesomeIcon
                                        icon={faEye}
                                        className="text-xl"
                                      />
                                    </motion.button>
                                  </Tooltip>
                                  <Tooltip title="Delete File" arrow>
                                    <motion.button
                                      whileHover={{
                                        scale: 1.2,
                                        color: "#DC2626",
                                      }}
                                      whileTap={{ scale: 0.9 }}
                                      type="button"
                                      onClick={() => removeFile(name)}
                                      className="text-blue-500 hover:text-red-700 focus:outline-none"
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        className="text-xl"
                                      />
                                    </motion.button>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                            <div className="mt-1 text-sm text-white">
                              <span>
                                {fileInputs[name]?.name || "No file chosen"}
                              </span>
                            </div>
                          </>
                        ) : (
                          <input
                            type={input.fieldType}
                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                            placeholder={input.fieldName}
                            name={name}
                            value={formData[name]}
                            onChange={(e) => handleInputChange(e, name)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Additional File Uploads */}
              <div className="flex flex-wrap mt-6">
                {/* Image Upload */}
                {eventData.allow_image_upload && (
                  <div className="w-full lg:w-6/12 px-4">
                    <div className="relative w-full mb-3">
                      <label className="block text-white text-xs font-bold mb-2">
                        Upload Images (JPG, PNG, GIF)
                      </label>
                      <input
                        id="image-upload-input"
                        type="file"
                        className="hidden"
                        name="image_upload"
                        accept="image/jpeg,image/jpg,image/png,image/gif"
                        multiple
                        onChange={(e) => handleInputChange(e, "image_upload")}
                      />
                      <div className="flex flex-wrap items-center gap-4 mt-2">
                        <label
                          htmlFor="image-upload-input"
                          className="mr-4"
                        >
                          <span className="cursor-pointer bg-green-500 text-white active:bg-green-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md">
                            <FontAwesomeIcon
                              icon={faArrowUpFromBracket}
                            />{" "}
                            Upload Images
                          </span>
                        </label>

                        {fileInputs["image_upload"] && (
                          <div className="flex items-center">
                            <Tooltip title="View Files" arrow>
                              <motion.button
                                whileHover={{
                                  scale: 1.2,
                                  color: "#FACC15",
                                }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => viewFile("image_upload")}
                                className="text-blue-500 hover:text-yellow-500 focus:outline-none mr-4"
                              >
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="text-xl"
                                />
                              </motion.button>
                            </Tooltip>
                            <Tooltip title="Delete Files" arrow>
                              <motion.button
                                whileHover={{
                                  scale: 1.2,
                                  color: "#DC2626",
                                }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => removeFile("image_upload")}
                                className="text-blue-500 hover:text-red-700 focus:outline-none"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="text-xl"
                                />
                              </motion.button>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-white">
                        <span>
                          {fileInputs["image_upload"]
                            ? `${fileInputs["image_upload"].length} image(s) selected`
                            : "No images chosen"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* PowerPoint Upload */}
                {eventData.allow_ppt_upload && (
                  <div className="w-full lg:w-6/12 px-4">
                    <div className="relative w-full mb-3">
                      <label className="block text-white text-xs font-bold mb-2">
                        Upload PowerPoint (PPT, PPTX)
                      </label>
                      <input
                        id="ppt-upload-input"
                        type="file"
                        className="hidden"
                        name="ppt_upload"
                        accept=".ppt,.pptx"
                        onChange={(e) => handleInputChange(e, "ppt_upload")}
                      />
                      <div className="flex flex-wrap items-center gap-4 mt-2">
                        <label
                          htmlFor="ppt-upload-input"
                          className="mr-4"
                        >
                          <span className="cursor-pointer bg-orange-500 text-white active:bg-orange-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md">
                            <FontAwesomeIcon
                              icon={faArrowUpFromBracket}
                            />{" "}
                            Upload PowerPoint
                          </span>
                        </label>

                        {fileInputs["ppt_upload"] && (
                          <div className="flex items-center">
                            <Tooltip title="View File" arrow>
                              <motion.button
                                whileHover={{
                                  scale: 1.2,
                                  color: "#FACC15",
                                }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => viewFile("ppt_upload")}
                                className="text-blue-500 hover:text-yellow-500 focus:outline-none mr-4"
                              >
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="text-xl"
                                />
                              </motion.button>
                            </Tooltip>
                            <Tooltip title="Delete File" arrow>
                              <motion.button
                                whileHover={{
                                  scale: 1.2,
                                  color: "#DC2626",
                                }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => removeFile("ppt_upload")}
                                className="text-blue-500 hover:text-red-700 focus:outline-none"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="text-xl"
                                />
                              </motion.button>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-white">
                        <span>
                          {fileInputs["ppt_upload"]?.name || "No PowerPoint file chosen"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end w-full">
                <Tooltip title="Back" arrow>
                  <button
                    className="mt-10 text-white bg-blueGray-700 active:bg-blueGray-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-auto"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(-1);
                      //window.location.href = `/${role}/events`;
                    }}
                  >
                    {"<< "}Back
                  </button>
                </Tooltip>

                <Tooltip title="Submit" arrow>
                  <button
                    className="bg-lightBlue-500 mt-10 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1"
                    type="submit"
                  >
                    Submit
                  </button>
                </Tooltip>

                <Tooltip title="Clear" arrow>
                  <button
                    className="bg-red-500 mt-10 text-white active:bg-red-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1"
                    type="button"
                    onClick={() => {
                      setFormData({});
                      setFileInputs({});
                    }}
                  >
                    Clear
                  </button>
                </Tooltip>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
